import { useEffect, useState } from "react";
import { FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import CriticalPinDialog from "../CriticalPinDialog";

const RFQ_TR = {
    open: { label: "Açık", cls: "border-blue-200 bg-blue-100 text-blue-800" },
    quoted: { label: "Teklif Geldi", cls: "border-amber-200 bg-amber-100 text-amber-800" },
    accepted: { label: "Kabul Edildi", cls: "border-emerald-200 bg-emerald-100 text-emerald-800" },
    rejected: { label: "Reddedildi", cls: "border-red-200 bg-red-100 text-red-700" },
    cancelled: { label: "İptal", cls: "border-stone-200 bg-stone-100 text-stone-600" },
};
const QUOTE_TR = {
    pending: { label: "Yanıt Bekliyor", cls: "border-amber-200 bg-amber-100 text-amber-800" },
    accepted: { label: "Kabul", cls: "border-emerald-200 bg-emerald-100 text-emerald-800" },
    rejected: { label: "Red", cls: "border-red-200 bg-red-100 text-red-700" },
};

export default function RfqsTab() {
    const [rfqs, setRfqs] = useState(null);
    const [pinTarget, setPinTarget] = useState(null);
    const load = () => api.get("/admin/rfqs").then((r) => setRfqs(r.data)).catch(() => setRfqs([]));
    useEffect(() => { load(); }, []);

    const deleteRecord = async (pin) => {
        try {
            await api.delete(`/admin/records/rfqs/${pinTarget.rfq_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success(`${pinTarget.rfq_no} silindi`);
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    if (!rfqs) return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted" />)}</div>;

    return (
        <div className="space-y-3" data-testid="admin-rfqs-tab">
            <p className="text-sm text-muted-foreground">Mağaza ve tedarikçi arasındaki tüm teklif süreçleri (salt görüntüleme).</p>
            {rfqs.map((r) => (
                <div key={r.rfq_id} className="rounded-2xl border bg-card p-5 shadow-sm" data-testid={`admin-rfq-${r.rfq_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <div className="flex items-center gap-2">
                                <p className="font-display font-bold">{r.rfq_no}</p>
                                <Badge variant="outline" className={`text-[10px] ${RFQ_TR[r.status]?.cls}`} data-testid={`admin-rfq-status-${r.rfq_id}`}>{RFQ_TR[r.status]?.label || r.status}</Badge>
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground">{r.buyer?.name || r.buyer_user_id} • {r.items?.length || 0} kalem • {fmtDate(r.created_at)}</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-8 border-red-200 text-xs text-red-600 hover:bg-red-50" onClick={() => setPinTarget(r)} data-testid={`rfq-delete-${r.rfq_id}`}>
                            <Trash2 className="mr-1 h-3 w-3" /> Sil
                        </Button>
                    </div>
                    {r.quotes?.length > 0 && (
                        <div className="mt-3 space-y-1.5 border-t pt-3">
                            {r.quotes.map((q) => (
                                <div key={q.quote_id} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-xs" data-testid={`admin-quote-${q.quote_id}`}>
                                    <span className="font-medium">{q.supplier_name}</span>
                                    <span className="font-display font-bold text-terra-600">{fmtTL(q.total)}</span>
                                    <Badge variant="outline" className={`text-[10px] ${QUOTE_TR[q.status]?.cls}`}>{QUOTE_TR[q.status]?.label || q.status}</Badge>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            {!rfqs.length && (
                <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="admin-rfqs-empty">
                    <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm text-muted-foreground">Henüz teklif talebi yok.</p>
                </div>
            )}
            <CriticalPinDialog
                open={!!pinTarget}
                onClose={() => setPinTarget(null)}
                title="RFQ Kaydını Sil"
                message={pinTarget ? `${pinTarget.rfq_no} numaralı teklif talebi kalıcı olarak silinecek. Bağlı tedarikçi teklifleri ayrıca silinmelidir. Bu işlem denetim kaydına alınır.` : ""}
                onSubmit={deleteRecord}
            />
        </div>
    );
}
